import { LoadingButton } from '@mui/lab';
import Masonry from '@mui/lab/Masonry';
import { Box, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useThemeStore from 'Theme/store';
import { cn } from 'utils/styles';
import useContainerWidth from '../hooks/useContainerWidth';
import AudioCard from './AudioCard';
import AudioCardSkeleton from './AudioCardSkeleton';
import ImageCard from './ImageCard';
import ImageCardSkeleton from './ImageCardSkeleton';
import VideoCard from './VideoCard';
import VideoCardSkeleton from './VideoCardSkeleton';

// Constants for layout
const SPACING_NUMBER = 2;
const SPACING = 16; // 2 * 8px (MUI spacing={2} = 16px)

function GalleryGrid({
  media,
  tab,
  loading,
  hasMore,
  isFilterApplied,
  onMediaClick,
  fetchMoreData,
  store,
}) {
  const { theme } = useThemeStore();

  const containerRef = useRef(null);
  const hasMoreRef = useRef(null);
  const cardRefs = useRef([]);
  const containerWidth = useContainerWidth(containerRef);

  const [scrollLimit, setScrollLimit] = useState(null);

  const filteredItems = useMemo(() => {
    if (tab === 'all') {
      return media;
    }
    return media.filter((item) => item.type === tab);
  }, [tab, media]);

  const masonryColumns = useMemo(() => {
    // Define minimum width for each content type
    const minWidths = {
      image: 300,
      video: 300,
      audio: 350,
      all: 350,
    };

    // Get minimum width based on content type
    const minWidth = minWidths[tab] || minWidths.all;

    // Column spacing in pixels
    const spacing = SPACING;

    // Calculate number of columns based on container width
    // We need to subtract total spacing (spacing * (n-1)) from available width
    // and divide by minimum width of each column
    const columns = Math.max(
      1,
      Math.floor((containerWidth + spacing) / (minWidth + spacing))
    );

    return columns;
  }, [tab, containerWidth]);

  const containerHeight = useMemo(() => {
    const hasMoreElement = hasMoreRef.current;
    if (!hasMoreElement || !hasMore) return 'h-full';

    const rect = hasMoreElement.getBoundingClientRect();
    const height = rect.height;

    return `h-[${scrollLimit + height}px]`;
  }, [scrollLimit, hasMoreRef, hasMore]);

  const masonryRendered = useCallback(() => {
    const refsArray = Object.values(cardRefs.current);
    if (!refsArray.length) return false;

    const validRefs = refsArray.filter((ref) => ref);
    if (!validRefs.length) return false;

    return validRefs.every((ref) => {
      const orderValue =
        ref.style && ref.style.order !== undefined && ref.style.order !== '';
      return orderValue;
    });
  }, []);

  const getScrollLimit = useCallback((columns) => {
    const refsArray = Object.values(cardRefs.current);
    if (!refsArray.length) return null;

    // Filter null elements
    const validRefs = refsArray.filter((ref) => ref);
    if (!validRefs.length) return null;

    // Group elements by columns based on their X position
    const columnGroups = {};
    for (let i = 1; i <= columns; i++) {
      columnGroups[i] = [];
    }

    validRefs.forEach((ref) => {
      const rect = ref.getBoundingClientRect();
      const orderValue = parseInt(ref.style.order || '0', 10);
      if (orderValue > 0 && orderValue <= columns) {
        columnGroups[orderValue].push({
          ref,
          bottom: rect.bottom,
          height: rect.height,
          top: rect.top,
          left: rect.left,
          order: parseInt(ref.style.order || '0', 10),
        });
      }
    });

    // Find the element with the lowest bottom position in each column
    const lastRowCandidates = Object.values(columnGroups).map((column) => {
      return column.reduce(
        (max, item) => (item.bottom > max.bottom ? item : max),
        column[0]
      );
    });

    // Among the last row candidates, find the one with the smallest height
    const smallest = lastRowCandidates.reduce(
      (min, item) => (item.bottom < min.bottom ? item : min),
      lastRowCandidates[0]
    );

    return smallest;
  }, []);

  useEffect(() => {
    const isReady = setInterval(() => {
      // console.log('waiting for masonry to be rendered');
      if (masonryRendered()) {
        clearInterval(isReady);
        // console.log('masonryRendered');
        const smallest = getScrollLimit(masonryColumns);
        const container = containerRef.current.getBoundingClientRect();
        const verticalLimit = smallest.bottom - container.top + SPACING;
        setScrollLimit(verticalLimit);
      }
    }, 100);
    return () => clearInterval(isReady);
  }, [
    filteredItems,
    masonryColumns,
    containerWidth,
    getScrollLimit,
    masonryRendered,
  ]);

  const renderSkeleton = (index) => {
    if (tab === 'all') {
      const skeletonTypes = ['image', 'audio', 'video'];
      const type = skeletonTypes[index % 3];

      switch (type) {
        case 'image':
          return <ImageCardSkeleton />;
        case 'audio':
          return <AudioCardSkeleton />;
        case 'video':
          return <VideoCardSkeleton />;
        default:
          return <ImageCardSkeleton />;
      }
    }

    switch (tab) {
      case 'image':
        return <ImageCardSkeleton />;
      case 'audio':
        return <AudioCardSkeleton />;
      case 'video':
        return <VideoCardSkeleton />;
      default:
        return <ImageCardSkeleton />;
    }
  };

  if (filteredItems.length === 0 && !loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          py: 4,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {isFilterApplied
            ? 'No content available with current filters.'
            : 'Your gallery is empty.'}
        </Typography>
      </Box>
    );
  }

  const renderItem = (item) => {
    switch (item.type) {
      case 'image':
        return <ImageCard image={item} onClick={onMediaClick} store={store} />;
      case 'audio':
        return <AudioCard audio={item} onClick={onMediaClick} store={store} />;
      case 'video':
        return <VideoCard video={item} onClick={onMediaClick} store={store} />;
      default:
        return null;
    }
  };

  return (
    <div ref={containerRef} className="px-1 md:px-3 relative w-full">
      <div className={`w-full ${containerHeight} overflow-hidden`}>
        <Masonry columns={masonryColumns} spacing={SPACING_NUMBER}>
          {filteredItems.map((item, index) => (
            <div
              id={item._id}
              key={`${item.type}-${item._id}-${index}`}
              ref={(el) => (cardRefs.current[item._id] = el)}
            >
              {renderItem(item)}
            </div>
          ))}
          {loading &&
            filteredItems.length === 0 &&
            Array(5)
              .fill()
              .map((_, index) => (
                <div key={`skeleton-${index}`}>{renderSkeleton(index)}</div>
              ))}
        </Masonry>
      </div>

      {masonryRendered() && hasMore && (
        <div
          ref={hasMoreRef}
          className={cn(
            'absolute bottom-0 left-0 w-full h-16 flex justify-center items-center',
            {
              'bg-gradient-to-t from-lead to-transparent': theme === 'dark',
              'bg-gradient-to-t from-seasalt to-transparent': theme === 'light',
            }
          )}
        >
          {/* Get more */}
          {hasMore && (
            <LoadingButton
              loading={loading}
              onClick={fetchMoreData}
              disabled={loading}
              variant="outlined"
              color="primary"
              sx={{
                mt: 3,
                mb: 2,
                borderRadius: '10px',
                alignSelf: 'center',
                backgroundColor: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                animation: 'bounce 2s infinite',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)',
                  },
                  '40%': {
                    transform: 'translateY(-10px)',
                  },
                  '60%': {
                    transform: 'translateY(-5px)',
                  },
                },
                '&:hover': {
                  backgroundColor: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  opacity: 0.8,
                  animation: 'none',
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease-in-out',
                },
              }}
            >
              Load More
            </LoadingButton>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(GalleryGrid, (prevProps, nextProps) => {
  return (
    prevProps.tab === nextProps.tab &&
    prevProps.loading === nextProps.loading &&
    prevProps.hasMore === nextProps.hasMore &&
    prevProps.media.length === nextProps.media.length
  );
});
